import { Button, Typography } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import { appLogger } from "@shared/services/app-logger";
import { appConfig } from "@shared/utils/appConfig";
import { useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";

const Container = styled.div`
  background-color: ${(p) => p.theme.colors.background};
  display: flex;
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ErrorMessage = styled.div`
  display: flex;
  background-color: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border2};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 600px;
  padding: 3rem 2rem;
  border-radius: 1rem;
  text-align: center;
`;

type ErrorBoundaryFallbackProps = {
  error: Error;
  resetErrorBoundary: () => void;
  onResetError?: () => void;
};

const ErrorBoundaryFallback = (props: ErrorBoundaryFallbackProps) => {
  const { error, resetErrorBoundary, onResetError } = props;

  const handleClickResetError = useCallback(() => {
    resetErrorBoundary();
    onResetError?.();
  }, [resetErrorBoundary, onResetError]);

  return (
    <Container role="alert">
      <ErrorMessage>
        <Typography variant="h4">Something went wrong.</Typography>
        {appConfig.NODE_ENV === "development" && (
          <Typography color="error" mt={2} mb={2}>
            <code>{error.message || error.toString()}</code>
          </Typography>
        )}
        <Button onClick={handleClickResetError} variant="outlined">
          Back
        </Button>
      </ErrorMessage>
    </Container>
  );
};

type ErrorBoundaryProviderProps = {
  children: React.ReactNode;
  onResetError?: () => void;
};
export const ErrorBoundaryProvider = (props: ErrorBoundaryProviderProps) => {
  const { children, onResetError } = props;

  const logError = useCallback(
    (error: Error, info: { componentStack?: string | null }) => {
      if (appConfig.NODE_ENV === "development") {
        console.error(error, info);
      }
      appLogger.error("[Application crashed]", { info, error });
    },
    []
  );

  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ErrorBoundaryFallback {...props} onResetError={onResetError} />
      )}
      onError={logError}
    >
      {children}
    </ErrorBoundary>
  );
};
